import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../utils/axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

// Async thunks for admin operations

// Fetch pending sellers
export const fetchPendingSellers = createAsyncThunk(
  'admin/fetchPendingSellers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/sellers/pending`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pending sellers');
    }
  }
);

// Approve seller
export const approveSeller = createAsyncThunk(
  'admin/approveSeller',
  async (sellerId, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const approvedBy = auth.user?.id || 'admin';
      const response = await axios.post(`${API_BASE_URL}/users/sellers/${sellerId}/approve`, 
        { approvedBy }, 
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return { sellerId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve seller');
    }
  }
);

// Reject seller
export const rejectSeller = createAsyncThunk(
  'admin/rejectSeller',
  async ({ sellerId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/sellers/${sellerId}/reject`, 
        { reason },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
        }
      );
      return { sellerId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject seller');
    }
  }
);

// Fetch approved sellers
export const fetchApprovedSellers = createAsyncThunk(
  'admin/fetchApprovedSellers',
  async ({ page = 0, size = 1000 } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/sellers/approved`, {
        params: { page, size },
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch approved sellers');
    }
  }
);

// Fetch rejected sellers
export const fetchRejectedSellers = createAsyncThunk(
  'admin/fetchRejectedSellers',
  async ({ page = 0, size = 1000 } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/sellers/rejected`, {
        params: { page, size },
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch rejected sellers');
    }
  }
);

// Fetch pending products
export const fetchPendingProducts = createAsyncThunk(
  'admin/fetchPendingProducts',
  async ({ page = 0, size = 200, fetchAll = false } = {}, { rejectWithValue }) => {
    try {
      const fetchPage = async (pageNumber) => {
        const response = await axios.get(`${API_BASE_URL}/products/pending`, {
          params: { page: pageNumber, size },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        return response.data;
      };

      const initialData = await fetchPage(page);

      if (!fetchAll) {
        return initialData;
      }

      // If response is already a simple array, return as-is
      if (Array.isArray(initialData)) {
        return initialData;
      }

      const totalPages = Number(initialData?.totalPages ?? 1);
      const initialContent = Array.isArray(initialData?.content) ? initialData.content : [];

      if (totalPages <= 1 || initialContent.length === 0) {
        return initialData;
      }

      const aggregatedContent = [...initialContent];

      for (let nextPage = page + 1; nextPage < totalPages; nextPage += 1) {
        const nextData = await fetchPage(nextPage);
        if (Array.isArray(nextData?.content) && nextData.content.length) {
          aggregatedContent.push(...nextData.content);
        }
      }

      return {
        ...initialData,
        content: aggregatedContent,
        page: 0,
        totalPages: 1,
        totalElements: aggregatedContent.length,
        size: aggregatedContent.length,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pending products');
    }
  }
);

// Fetch all approved products (admin - all sellers)
export const fetchAdminApprovedProducts = createAsyncThunk(
  'admin/fetchApprovedProducts',
  async ({ page = 0, size = 1000 } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/products`, {
        params: { approvalStatus: 'APPROVED', page, size },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch approved products');
    }
  }
);

// Fetch all rejected products (admin - all sellers)
export const fetchAdminRejectedProducts = createAsyncThunk(
  'admin/fetchRejectedProducts',
  async ({ page = 0, size = 1000 } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/products`, {
        params: { approvalStatus: 'REJECTED', page, size },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch rejected products');
    }
  }
);

// Approve product
export const approveProduct = createAsyncThunk(
  'admin/approveProduct',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/products/${productId}/approve`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return { productId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve product');
    }
  }
);

// Reject product
export const rejectProduct = createAsyncThunk(
  'admin/rejectProduct',
  async ({ productId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/products/${productId}/reject`, 
        { reason },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return { productId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject product');
    }
  }
);

// Fetch pending reviews
export const fetchPendingReviews = createAsyncThunk(
  'admin/fetchPendingReviews',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/reviews/admin/pending`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pending reviews');
    }
  }
);

// Approve review
export const approveReview = createAsyncThunk(
  'admin/approveReview',
  async (reviewId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const moderatorId = state.auth.user?.id || localStorage.getItem('userId');
      
      const response = await axios.post(`${API_BASE_URL}/reviews/${reviewId}/approve?moderatorId=${moderatorId}`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return { reviewId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve review');
    }
  }
);

// Reject review
export const rejectReview = createAsyncThunk(
  'admin/rejectReview',
  async ({ reviewId, reason }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const moderatorId = state.auth.user?.id || localStorage.getItem('userId');
      
      const response = await axios.post(`${API_BASE_URL}/reviews/${reviewId}/reject?moderatorId=${moderatorId}`, 
        { reason },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return { reviewId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject review');
    }
  }
);

// Fetch admin analytics
export const fetchAdminAnalytics = createAsyncThunk(
  'admin/fetchAdminAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const [ordersResponse, paymentsResponse, usersResponse, productsResponse, activeSellersResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/orders/analytics`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }).catch((err) => {
          console.error('Error fetching orders analytics:', err);
          return { data: {} };
        }),
        axios.get(`${API_BASE_URL}/payments/admin/analytics`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }).catch((err) => {
          console.error('Error fetching payments analytics:', err);
          return { data: {} };
        }),
        axios.get(`${API_BASE_URL}/users/analytics/counts`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }).catch((err) => {
          console.error('Error fetching users analytics:', err);
          return { data: {} };
        }),
        // Fetch products with size=1 to get totalElements count
        axios.get(`${API_BASE_URL}/products`, {
          params: { page: 0, size: 1 },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }).catch((err) => {
          console.error('Error fetching products count:', err);
          return { data: { totalElements: 0 } };
        }),
        // Fetch active sellers to get count
        axios.get(`${API_BASE_URL}/users/sellers/active`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }).catch((err) => {
          console.error('Error fetching active sellers:', err);
          return { data: [] };
        })
      ]);
      
      // Debug logging
      console.log('Analytics API Responses:', {
        usersResponse: usersResponse.data,
        productsResponse: productsResponse.data,
        activeSellersResponse: activeSellersResponse.data
      });
      
      // Get counts from usersResponse (now includes totalProducts and activeSellers)
      const totalProducts = usersResponse.data?.totalProducts || productsResponse.data?.totalElements || productsResponse.data?.total || 0;
      const activeSellers = usersResponse.data?.activeSellers || (Array.isArray(activeSellersResponse.data) 
        ? activeSellersResponse.data.length 
        : (usersResponse.data?.totalSellers || 0));
      
      console.log('Calculated counts:', {
        totalUsers: usersResponse.data?.totalUsers,
        totalSellers: activeSellers,
        totalProducts: totalProducts,
        usersResponseData: usersResponse.data
      });
      
       // Combine all analytics data
       const combinedAnalytics = {
         // Order analytics
         totalOrders: ordersResponse?.data?.totalOrders || 0,
         totalRevenue: ordersResponse?.data?.totalRevenue || paymentsResponse?.data?.monthlyRevenue || 0,
         avgOrderValue: ordersResponse?.data?.averageOrderValue || 0,
         orderGrowthRate: ordersResponse?.data?.growthRate || 0,
         
         // Payment analytics
         totalPayments: paymentsResponse?.data?.totalPayments || 0,
         successfulPayments: paymentsResponse?.data?.successfulPayments || 0,
         failedPayments: paymentsResponse?.data?.failedPayments || 0,
         pendingPayments: paymentsResponse?.data?.pendingPayments || 0,
         monthlyRevenue: paymentsResponse?.data?.monthlyRevenue || 0,
         revenueGrowthRate: paymentsResponse?.data?.growthRate || 0,
         successRate: paymentsResponse?.data?.successRate || 0,
         
         // Financial metrics for admin dashboard
         totalRefunded: paymentsResponse?.data?.totalRefunded || 0,
         refundRate: paymentsResponse?.data?.refundRate || 0,
         commissionEarned: (paymentsResponse?.data?.monthlyRevenue || 0) * 0.05, // 5% commission rate
         pendingPayouts: paymentsResponse?.data?.pendingPayments || 0,
         
         // Payment method and status distributions
         statusDistribution: paymentsResponse?.data?.statusDistribution || {},
         methodDistribution: paymentsResponse?.data?.methodDistribution || {},
         
         // User analytics - THIS IS THE KEY DATA
         totalUsers: usersResponse?.data?.totalUsers || 0,
         totalSellers: usersResponse?.data?.totalSellers || 0, // All sellers
         activeSellers: activeSellers, // Active sellers count
         totalProducts: totalProducts, // Get from products endpoint
         userGrowthRate: usersResponse?.data?.userGrowthRate || 0,
         sellerGrowthRate: usersResponse?.data?.sellerGrowthRate || 0,
         productGrowthRate: usersResponse?.data?.productGrowthRate || 0,
         
         // Additional metrics
         activeDisputes: ordersResponse?.data?.activeDisputes || 0,
         conversionRate: ordersResponse?.data?.conversionRate || 0,
         
         // Keep original structure for backward compatibility
         orders: ordersResponse?.data || {},
         payments: paymentsResponse?.data || {},
         users: usersResponse?.data || {}
       };
       
       console.log('Combined Analytics Object:', combinedAnalytics);
       console.log('Key values:', {
         totalUsers: combinedAnalytics.totalUsers,
         activeSellers: combinedAnalytics.activeSellers,
         totalProducts: combinedAnalytics.totalProducts
       });
       
       // Ensure we return a valid object even if some API calls failed
       if (!combinedAnalytics || typeof combinedAnalytics !== 'object') {
         console.error('Invalid combinedAnalytics:', combinedAnalytics);
         return rejectWithValue('Invalid analytics data received');
       }
       
       return combinedAnalytics;
     } catch (error) {
       console.error('Error in fetchAdminAnalytics:', error);
       console.error('Error response:', error.response);
       return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch admin analytics');
     }
  }
);

// Fetch monthly revenue from completed orders
export const fetchMonthlyRevenueFromCompletedOrders = createAsyncThunk(
  'admin/fetchMonthlyRevenueFromCompletedOrders',
  async (year = new Date().getFullYear(), { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/orders/analytics/monthly-revenue?year=${year}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch monthly revenue from completed orders');
    }
  }
);

// Fetch all users
export const fetchAllUsers = createAsyncThunk(
  'admin/fetchAllUsers',
  async ({ page = 0, size = 20, role }, { rejectWithValue }) => {
    try {
      const params = { page, size };
      if (role) params.role = role;
      
      const response = await axios.get(`${API_BASE_URL}/users`, {
        params,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users');
    }
  }
);

// Fetch all payments
export const fetchAllPayments = createAsyncThunk(
  'admin/fetchAllPayments',
  async ({ page = 0, size = 20, status, method }, { rejectWithValue }) => {
    try {
      const params = { page, size };
      if (status) params.status = status;
      if (method) params.method = method;
      
      const response = await axios.get(`${API_BASE_URL}/payments/admin/all`, {
        params,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payments');
    }
  }
);

// Fetch pending orders
export const fetchPendingOrders = createAsyncThunk(
  'admin/fetchPendingOrders',
  async ({ page = 0, size = 20 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/orders/pending`, {
        params: { page, size },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pending orders');
    }
  }
);

const initialState = {
  // Sellers
  pendingSellers: [],
  pendingSellersLoading: false,
  pendingSellersError: null,
  approvedSellers: [],
  approvedSellersLoading: false,
  approvedSellersError: null,
  rejectedSellers: [],
  rejectedSellersLoading: false,
  rejectedSellersError: null,
  
  // Products
  pendingProducts: [],
  pendingProductsLoading: false,
  pendingProductsError: null,
  adminApprovedProducts: [],
  adminApprovedProductsLoading: false,
  adminApprovedProductsError: null,
  adminRejectedProducts: [],
  adminRejectedProductsLoading: false,
  adminRejectedProductsError: null,
  
  // Reviews
  pendingReviews: [],
  pendingReviewsLoading: false,
  pendingReviewsError: null,
  
  // Analytics
  analytics: {
    orders: null,
    payments: null
  },
  analyticsLoading: false,
  analyticsError: null,
  
  // Monthly revenue from completed orders
  monthlyRevenueFromCompletedOrders: null,
  monthlyRevenueFromCompletedOrdersLoading: false,
  monthlyRevenueFromCompletedOrdersError: null,
  
  // Users
  users: [],
  usersLoading: false,
  usersError: null,
  usersPagination: {
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  },
  
  // Payments
  payments: [],
  paymentsLoading: false,
  paymentsError: null,
  paymentsPagination: {
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  },
  
  // Orders
  pendingOrders: [],
  pendingOrdersLoading: false,
  pendingOrdersError: null,
  
  // General
  loading: false,
  error: null
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearAdminData: (state) => {
      return initialState;
    },
    clearAdminError: (state) => {
      state.error = null;
      state.pendingSellersError = null;
      state.pendingProductsError = null;
      state.pendingReviewsError = null;
      state.analyticsError = null;
      state.monthlyRevenueFromCompletedOrdersError = null;
      state.usersError = null;
      state.paymentsError = null;
      state.pendingOrdersError = null;
    },
    removePendingSeller: (state, action) => {
      state.pendingSellers = state.pendingSellers.filter(
        seller => seller.id !== action.payload
      );
    },
    removePendingProduct: (state, action) => {
      state.pendingProducts = state.pendingProducts.filter(
        product => product.id !== action.payload
      );
    },
    removePendingReview: (state, action) => {
      state.pendingReviews = state.pendingReviews.filter(
        review => review.id !== action.payload
      );
    }
  },
  extraReducers: (builder) => {
    builder
      // Pending Sellers
      .addCase(fetchPendingSellers.pending, (state) => {
        state.pendingSellersLoading = true;
        state.pendingSellersError = null;
      })
      .addCase(fetchPendingSellers.fulfilled, (state, action) => {
        state.pendingSellersLoading = false;
        state.pendingSellers = action.payload;
      })
      .addCase(fetchPendingSellers.rejected, (state, action) => {
        state.pendingSellersLoading = false;
        state.pendingSellersError = action.payload;
      })
      
      // Approve Seller
      .addCase(approveSeller.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveSeller.fulfilled, (state, action) => {
        state.loading = false;
        // Remove from pending sellers
        if (Array.isArray(state.pendingSellers)) {
          state.pendingSellers = state.pendingSellers.filter(
            seller => seller.id !== action.payload.sellerId
          );
        } else if (state.pendingSellers?.content) {
          state.pendingSellers = {
            ...state.pendingSellers,
            content: state.pendingSellers.content.filter(seller => seller.id !== action.payload.sellerId),
            totalElements: Math.max(0, (state.pendingSellers.totalElements || 1) - 1)
          };
        }
        // Remove from rejected sellers (if approving a previously rejected seller)
        if (Array.isArray(state.rejectedSellers)) {
          state.rejectedSellers = state.rejectedSellers.filter(
            seller => seller.id !== action.payload.sellerId
          );
        } else if (state.rejectedSellers?.content) {
          state.rejectedSellers = {
            ...state.rejectedSellers,
            content: state.rejectedSellers.content.filter(seller => seller.id !== action.payload.sellerId),
            totalElements: Math.max(0, (state.rejectedSellers.totalElements || 1) - 1)
          };
        }
      })
      .addCase(approveSeller.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Reject Seller
      .addCase(rejectSeller.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectSeller.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(state.pendingSellers)) {
          state.pendingSellers = state.pendingSellers.filter(
            seller => seller.id !== action.payload.sellerId
          );
        } else if (state.pendingSellers?.content) {
          state.pendingSellers = {
            ...state.pendingSellers,
            content: state.pendingSellers.content.filter(seller => seller.id !== action.payload.sellerId),
            totalElements: Math.max(0, (state.pendingSellers.totalElements || 1) - 1)
          };
        }
      })
      .addCase(rejectSeller.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Approved Sellers
      .addCase(fetchApprovedSellers.pending, (state) => {
        state.approvedSellersLoading = true;
        state.approvedSellersError = null;
      })
      .addCase(fetchApprovedSellers.fulfilled, (state, action) => {
        state.approvedSellersLoading = false;
        const payload = action.payload;
        state.approvedSellers = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.content)
          ? payload.content
          : [];
      })
      .addCase(fetchApprovedSellers.rejected, (state, action) => {
        state.approvedSellersLoading = false;
        state.approvedSellersError = action.payload;
      })

      // Rejected Sellers
      .addCase(fetchRejectedSellers.pending, (state) => {
        state.rejectedSellersLoading = true;
        state.rejectedSellersError = null;
      })
      .addCase(fetchRejectedSellers.fulfilled, (state, action) => {
        state.rejectedSellersLoading = false;
        const payload = action.payload;
        state.rejectedSellers = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.content)
          ? payload.content
          : [];
      })
      .addCase(fetchRejectedSellers.rejected, (state, action) => {
        state.rejectedSellersLoading = false;
        state.rejectedSellersError = action.payload;
      })

      // Pending Products
      .addCase(fetchPendingProducts.pending, (state) => {
        state.pendingProductsLoading = true;
        state.pendingProductsError = null;
      })
      .addCase(fetchPendingProducts.fulfilled, (state, action) => {
        state.pendingProductsLoading = false;
        state.pendingProducts = action.payload;
      })
      .addCase(fetchPendingProducts.rejected, (state, action) => {
        state.pendingProductsLoading = false;
        state.pendingProductsError = action.payload;
      })

      // Admin Approved Products
      .addCase(fetchAdminApprovedProducts.pending, (state) => {
        state.adminApprovedProductsLoading = true;
        state.adminApprovedProductsError = null;
      })
      .addCase(fetchAdminApprovedProducts.fulfilled, (state, action) => {
        state.adminApprovedProductsLoading = false;
        const payload = action.payload;
        state.adminApprovedProducts = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.content)
          ? payload.content
          : [];
      })
      .addCase(fetchAdminApprovedProducts.rejected, (state, action) => {
        state.adminApprovedProductsLoading = false;
        state.adminApprovedProductsError = action.payload;
      })

      // Admin Rejected Products
      .addCase(fetchAdminRejectedProducts.pending, (state) => {
        state.adminRejectedProductsLoading = true;
        state.adminRejectedProductsError = null;
      })
      .addCase(fetchAdminRejectedProducts.fulfilled, (state, action) => {
        state.adminRejectedProductsLoading = false;
        const payload = action.payload;
        state.adminRejectedProducts = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.content)
          ? payload.content
          : [];
      })
      .addCase(fetchAdminRejectedProducts.rejected, (state, action) => {
        state.adminRejectedProductsLoading = false;
        state.adminRejectedProductsError = action.payload;
      })

      // Approve Product
      .addCase(approveProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveProduct.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(state.pendingProducts)) {
          state.pendingProducts = state.pendingProducts.filter(
            product => product.id !== action.payload.productId
          );
        } else if (state.pendingProducts?.content) {
          state.pendingProducts = {
            ...state.pendingProducts,
            content: state.pendingProducts.content.filter(product => product.id !== action.payload.productId),
            totalElements: Math.max(0, (state.pendingProducts.totalElements || 1) - 1)
          };
        }
      })
      .addCase(approveProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Reject Product
      .addCase(rejectProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectProduct.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(state.pendingProducts)) {
          state.pendingProducts = state.pendingProducts.filter(
            product => product.id !== action.payload.productId
          );
        } else if (state.pendingProducts?.content) {
          state.pendingProducts = {
            ...state.pendingProducts,
            content: state.pendingProducts.content.filter(product => product.id !== action.payload.productId),
            totalElements: Math.max(0, (state.pendingProducts.totalElements || 1) - 1)
          };
        }
      })
      .addCase(rejectProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Pending Reviews
      .addCase(fetchPendingReviews.pending, (state) => {
        state.pendingReviewsLoading = true;
        state.pendingReviewsError = null;
      })
      .addCase(fetchPendingReviews.fulfilled, (state, action) => {
        state.pendingReviewsLoading = false;
        state.pendingReviews = action.payload;
      })
      .addCase(fetchPendingReviews.rejected, (state, action) => {
        state.pendingReviewsLoading = false;
        state.pendingReviewsError = action.payload;
      })
      
      // Approve Review
      .addCase(approveReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveReview.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both array and paginated response formats
        if (Array.isArray(state.pendingReviews)) {
          state.pendingReviews = state.pendingReviews.filter(
            review => review.id !== action.payload.reviewId
          );
        } else if (state.pendingReviews?.reviews) {
          state.pendingReviews.reviews = state.pendingReviews.reviews.filter(
            review => review.id !== action.payload.reviewId
          );
        }
      })
      .addCase(approveReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Reject Review
      .addCase(rejectReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectReview.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both array and paginated response formats
        if (Array.isArray(state.pendingReviews)) {
          state.pendingReviews = state.pendingReviews.filter(
            review => review.id !== action.payload.reviewId
          );
        } else if (state.pendingReviews?.reviews) {
          state.pendingReviews.reviews = state.pendingReviews.reviews.filter(
            review => review.id !== action.payload.reviewId
          );
        }
      })
      .addCase(rejectReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Admin Analytics
      .addCase(fetchAdminAnalytics.pending, (state) => {
        state.analyticsLoading = true;
        state.analyticsError = null;
      })
       .addCase(fetchAdminAnalytics.fulfilled, (state, action) => {
         state.analyticsLoading = false;
         console.log('fetchAdminAnalytics fulfilled with payload:', action.payload);
         console.log('Payload type:', typeof action.payload);
         console.log('Payload keys:', action.payload ? Object.keys(action.payload) : 'null');
         
         // Ensure we're storing the full payload, not just orders/payments
         if (action.payload && typeof action.payload === 'object') {
           state.analytics = { ...action.payload };
           console.log('Analytics state updated:', state.analytics);
         } else {
           console.error('Invalid payload received:', action.payload);
           state.analytics = {};
         }
       })
      .addCase(fetchAdminAnalytics.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.analyticsError = action.payload;
      })
      
      // Monthly Revenue from Completed Orders
      .addCase(fetchMonthlyRevenueFromCompletedOrders.pending, (state) => {
        state.monthlyRevenueFromCompletedOrdersLoading = true;
        state.monthlyRevenueFromCompletedOrdersError = null;
      })
      .addCase(fetchMonthlyRevenueFromCompletedOrders.fulfilled, (state, action) => {
        state.monthlyRevenueFromCompletedOrdersLoading = false;
        state.monthlyRevenueFromCompletedOrders = action.payload;
      })
      .addCase(fetchMonthlyRevenueFromCompletedOrders.rejected, (state, action) => {
        state.monthlyRevenueFromCompletedOrdersLoading = false;
        state.monthlyRevenueFromCompletedOrdersError = action.payload;
      })
      
      // All Users
      .addCase(fetchAllUsers.pending, (state) => {
        state.usersLoading = true;
        state.usersError = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.users = action.payload.users || action.payload.content || [];
        state.usersPagination = {
          currentPage: action.payload.currentPage || 0,
          totalPages: action.payload.totalPages || 0,
          totalItems: action.payload.totalItems || 0,
          hasNext: action.payload.hasNext || false,
          hasPrevious: action.payload.hasPrevious || false
        };
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload;
      })
      
      // All Payments
      .addCase(fetchAllPayments.pending, (state) => {
        state.paymentsLoading = true;
        state.paymentsError = null;
      })
      .addCase(fetchAllPayments.fulfilled, (state, action) => {
        state.paymentsLoading = false;
        state.payments = action.payload.payments || action.payload.content || [];
        state.paymentsPagination = {
          currentPage: action.payload.currentPage || 0,
          totalPages: action.payload.totalPages || 0,
          totalItems: action.payload.totalItems || 0,
          hasNext: action.payload.hasNext || false,
          hasPrevious: action.payload.hasPrevious || false
        };
      })
      .addCase(fetchAllPayments.rejected, (state, action) => {
        state.paymentsLoading = false;
        state.paymentsError = action.payload;
      })
      
      // Pending Orders
      .addCase(fetchPendingOrders.pending, (state) => {
        state.pendingOrdersLoading = true;
        state.pendingOrdersError = null;
      })
      .addCase(fetchPendingOrders.fulfilled, (state, action) => {
        state.pendingOrdersLoading = false;
        state.pendingOrders = action.payload.orders || action.payload.content || [];
      })
      .addCase(fetchPendingOrders.rejected, (state, action) => {
        state.pendingOrdersLoading = false;
        state.pendingOrdersError = action.payload;
      });
  }
});

export const { 
  clearAdminData, 
  clearAdminError, 
  removePendingSeller, 
  removePendingProduct, 
  removePendingReview, 
} = adminSlice.actions;

export default adminSlice.reducer;